import { axiosBaseQuery } from '@client';
import { AppointmentResponse } from '@module/appointment/model/AppointmentResponse';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { VoyageResponse } from '@state/types';

export interface CancelCloseAppointmentsResponse {
  appointments: AppointmentResponse[];
  performedBy: string;
  performedAt: string;
}

export const appointmentApi = createApi({
  reducerPath: 'appointment-api',
  tagTypes: ['Appointment'],
  baseQuery: axiosBaseQuery({ baseUrl: 'secured/appointment' }),
  endpoints: (build) => ({
    loadAppointment: build.query<
      AppointmentResponse,
      { appointmentId: string }
    >({
      query: ({ appointmentId }) => ({
        method: 'GET',
        url: `/${appointmentId}/load`
      }),
      providesTags: ['Appointment']
    }),
    revertCancelledAppointment: build.mutation<
      AppointmentResponse,
      { appointmentId: number }
    >({
      query: ({ appointmentId }) => {
        return {
          url: `/${appointmentId}/revert`,
          method: 'POST'
        };
      }
    }),
    rejectAppointment: build.mutation<void, { appointmentId: string }>({
      query: ({ appointmentId }) => ({
        method: 'POST',
        url: `/${appointmentId}/reject`
      })
    }),
    closeAppointment: build.query<void, { appointmentId: string }>({
      query: ({ appointmentId }) => ({
        method: 'GET',
        url: `/${appointmentId}/close`
      })
    }),
    deleteAppointment: build.query<void, { appointmentId: number }>({
      query: ({ appointmentId }) => ({
        method: 'GET',
        url: `/${appointmentId}/delete`
      })
    }),
    checkMultiplePortCalls: build.query<
      { hasMultiplePortCalls: boolean },
      number
    >({
      query: (appointmentId) => ({
        url: `/${appointmentId}/check-multiple-portcalls`,
        method: 'GET'
      })
    }),
    amendVoyageNumber: build.mutation<
      VoyageResponse,
      { appointmentId: number; voyageNumber: string }
    >({
      query: ({ appointmentId, voyageNumber }) => ({
        url: `/${appointmentId}/amend-voyage-number`,
        method: 'POST',
        data: { voyageNumber }
      })
    }),
    revertRejectedAppointment: build.mutation<AppointmentResponse, number>({
      query: (appointmentId) => ({
        url: `/${appointmentId}/revert-rejected`,
        method: 'POST'
      })
    }),
    lookupAppointmentsToCancelOrClose: build.mutation<
      AppointmentResponse[],
      { appointmentIds: number[] }
    >({
      query: (data) => ({
        url: '/cancel-close/lookup',
        method: 'POST',
        data
      })
    }),
    cancelOrCloseAppointments: build.mutation<
      CancelCloseAppointmentsResponse,
      {
        appointmentIds: number[];
        targetState: 'CANCELLED' | 'CLOSED';
        reason: string;
        supportTicket?: string;
      }
    >({
      query: (data) => ({
        url: '/cancel-close',
        method: 'POST',
        data
      })
    })
  })
});

export const invalidateLoadAppointment = appointmentApi.util.invalidateTags([
  'Appointment'
]);

export const {
  useRevertCancelledAppointmentMutation,
  useRejectAppointmentMutation,
  useLazyCloseAppointmentQuery,
  useLoadAppointmentQuery,
  useLazyLoadAppointmentQuery,
  useLazyDeleteAppointmentQuery,
  useLazyCheckMultiplePortCallsQuery,
  useAmendVoyageNumberMutation,
  useRevertRejectedAppointmentMutation,
  useLookupAppointmentsToCancelOrCloseMutation,
  useCancelOrCloseAppointmentsMutation
} = appointmentApi;
